import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EventLoader } from "./components/EventLoader";
import { GlobalFilters } from "./components/GlobalFilters";
import { Navigation, type PageId } from "./components/Navigation";
import { filterRunners } from "./data/analytics";
import { fetchMetadata, fetchSnapshot } from "./data/client";
import { DEFAULT_EVENT_URL, EMPTY_FILTERS } from "./data/constants";
import { formatDateTime, formatNumber, getBrowserTimeZone } from "./data/formatters";
import { normalizeSnapshot } from "./data/normalize";
import { parseRankingUrl } from "./data/parser";
import { useDebouncedValue } from "./hooks/useDebouncedValue";
import { ActivityPage } from "./pages/ActivityPage";
import { DemographicsPage } from "./pages/DemographicsPage";
import { LocationPage } from "./pages/LocationPage";
import { OverviewPage } from "./pages/OverviewPage";
import { QualityPage } from "./pages/QualityPage";
import { RunnersPage } from "./pages/RunnersPage";
import type { EventSnapshot, FilterState, SourceStatus } from "./types";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const LAST_EVENT_KEY = "tatta-insight:last-event-url";

const INITIAL_STATUS: SourceStatus = {
  lastCheckedAt: null,
  responseTimeMs: null,
  usingPreviousSnapshot: false,
  updateMessage: "尚未載入資料",
};

function getInitialUrl() {
  try {
    return localStorage.getItem(LAST_EVENT_KEY) || DEFAULT_EVENT_URL;
  } catch {
    return DEFAULT_EVENT_URL;
  }
}

export default function App() {
  const [inputUrl, setInputUrl] = useState(getInitialUrl);
  const [snapshot, setSnapshot] = useState<EventSnapshot | null>(null);
  const [filters, setFilters] = useState<FilterState>({ ...EMPTY_FILTERS });
  const [page, setPage] = useState<PageId>("overview");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState<SourceStatus>(INITIAL_STATUS);
  const snapshotRef = useRef<EventSnapshot | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const debouncedQuery = useDebouncedValue(filters.query, 200);

  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  const loadEvent = useCallback(async (url: string, background = false) => {
    const reference = parseRankingUrl(url);
    const previous = snapshotRef.current?.eventKey === reference.eventKey ? snapshotRef.current : null;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    const startedAt = performance.now();
    if (!background) {
      setLoading(true);
      setError("");
      setStage("01 / 03 讀取官方 metadata");
    }

    try {
      const metadata = await fetchMetadata(reference, controller.signal);
      const checkedAt = new Date().toISOString();
      if (previous && new Date(metadata.aggregateDateEpochMsec).toISOString() === previous.aggregateAt) {
        setStatus({
          lastCheckedAt: checkedAt,
          responseTimeMs: Math.round(performance.now() - startedAt),
          usingPreviousSnapshot: false,
          updateMessage: "Metadata 無變更，目前快照已是最新",
        });
        return;
      }

      if (!background) setStage("02 / 03 下載並驗證完整快照");
      const rawSnapshot = await fetchSnapshot(reference, controller.signal);
      if (!background) setStage("03 / 03 標準化跑者資料");
      const next = normalizeSnapshot(reference, metadata, rawSnapshot);
      const droppedSignificantly = previous && next.achieverCount < previous.achieverCount * 0.5;
      setSnapshot(next);
      setFilters({ ...EMPTY_FILTERS });
      setStatus({
        lastCheckedAt: checkedAt,
        responseTimeMs: Math.round(performance.now() - startedAt),
        usingPreviousSnapshot: false,
        updateMessage: droppedSignificantly
          ? "新快照筆數較前一版大幅下降，請查核來源"
          : "Schema 驗證通過，已切換最新有效快照",
      });
      if (!background) setPage("overview");
      try {
        localStorage.setItem(LAST_EVENT_KEY, reference.sourceUrl);
      } catch {
        // 本機設定不可用時不影響主要功能。
      }
      console.info("TATTA snapshot ready", {
        raceId: reference.raceId,
        rankingType: reference.rankingType,
        fetchedAt: next.fetchedAt,
        recordCount: next.achieverCount,
        validation: "passed",
        elapsedMs: Math.round(performance.now() - startedAt),
      });
    } catch (reason) {
      if (controller.signal.aborted) return;
      const message = reason instanceof Error ? reason.message : "無法載入賽事資料。";
      const current = snapshotRef.current;
      setError(message);
      setStatus({
        lastCheckedAt: new Date().toISOString(),
        responseTimeMs: Math.round(performance.now() - startedAt),
        usingPreviousSnapshot: Boolean(current),
        updateMessage: previous
          ? "更新失敗，繼續使用目前分頁中的上一個有效快照"
          : current
            ? "賽事切換失敗，繼續顯示原賽事的有效快照"
            : "尚無可用快照",
      });
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      if (!background) {
        setLoading(false);
        setStage("");
      }
    }
  }, []);

  useEffect(() => {
    if (!snapshot) return;
    const timer = window.setInterval(() => {
      void loadEvent(snapshot.sourceUrl, true);
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadEvent, snapshot?.eventKey, snapshot?.sourceUrl]);

  useEffect(() => () => requestRef.current?.abort(), []);

  const effectiveFilters = useMemo(
    () => ({ ...filters, query: debouncedQuery }),
    [debouncedQuery, filters],
  );
  const filteredRunners = useMemo(
    () => snapshot ? filterRunners(snapshot.runners, effectiveFilters) : [],
    [effectiveFilters, snapshot],
  );

  const submit = () => {
    try {
      void loadEvent(inputUrl);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "網址格式不正確。");
    }
  };

  if (!snapshot) {
    return (
      <div className="app-shell app-shell--landing">
        <LandingHeader />
        <main className="landing">
          <section className="hero">
            <div className="hero__copy">
              <p className="eyebrow">INTERNAL RUNNER DATA CONSOLE / 2026</p>
              <h1>把賽事快照，<br /><span>跑成清楚的洞察。</span></h1>
              <p>輸入經授權使用的 TATTA 排行榜網址，在瀏覽器內完成搜尋、篩選、排序與視覺化。資料不經第三方服務，也不保存個人歷史。</p>
            </div>
            <div className="runner-art" aria-hidden="true">
              <div className="runner-art__sun" />
              <div className="pixel-runner"><i /><i /><i /><i /><i /><i /><i /></div>
              <div className="runner-art__track"><span /><span /><span /></div>
              <div className="runner-art__label"></div>
            </div>
          </section>

          <section className="landing-loader">
            <div className="landing-loader__number">01</div>
            <div>
              <p className="eyebrow">LOAD EVENT</p>
              <h2>載入第一場賽事</h2>
              <p className="text-muted">目前完整支援 distance-challenge。範例網址已填入，可直接開始。</p>
            </div>
            <EventLoader value={inputUrl} onChange={setInputUrl} onSubmit={submit} loading={loading} stage={stage} error={error} />
          </section>

          <section className="feature-strip">
            <article><span>01</span><h3>固定來源</h3><p>只從 TATTA 公開 S3 路徑讀取 JSON，不接受任意網址。</p></article>
            <article><span>02</span><h3>分頁記憶體</h3><p>跑者明細不寫入 localStorage、資料庫或第三方分析服務。</p></article>
            <article><span>03</span><h3>一致口徑</h3><p>每張圖表共用篩選母數，未知與未公開資料不會被藏起來。</p></article>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="dashboard-header">
        <a className="brand" href="/" onClick={(event) => { event.preventDefault(); setSnapshot(null); setError(""); }} aria-label="返回賽事載入首頁">
          <span className="brand__mark"><i /><i /><i /><i /></span>
          <span><b>TATTA</b><small>INSIGHT_</small></span>
        </a>
        <div className="dashboard-header__clock"><span>LOCAL TIMEZONE</span><strong>{getBrowserTimeZone()}</strong></div>
      </header>

      <main>
        <section className="event-banner">
          <div className="event-banner__title">
            <div className="event-badge">{snapshot.raceId}</div>
            <div>
              <p className="eyebrow">DISTANCE CHALLENGE / LIVE SNAPSHOT</p>
              <h1>{snapshot.title}</h1>
              <p>官方彙整 {formatDateTime(snapshot.aggregateAt)} · {formatNumber(snapshot.achieverCount)} 位達標者</p>
            </div>
          </div>
          <details className="switcher">
            <summary>切換賽事</summary>
            <EventLoader compact value={inputUrl} onChange={setInputUrl} onSubmit={submit} loading={loading} stage={stage} error={error} />
          </details>
        </section>

        {status.usingPreviousSnapshot && <div className="notice notice--warning">{status.updateMessage}。錯誤：{error}</div>}
        {!status.usingPreviousSnapshot && status.updateMessage.includes("下降") && <div className="notice notice--warning">{status.updateMessage}</div>}

        <Navigation current={page} onChange={setPage} />
        <div className="dashboard-body">
          <GlobalFilters filters={filters} onChange={setFilters} resultCount={filteredRunners.length} totalCount={snapshot.runners.length} />
          <div className="page-heading">
            <span>{String(["overview", "runners", "demographics", "location", "activity", "quality"].indexOf(page) + 1).padStart(2, "0")}</span>
            <div><p className="eyebrow">DASHBOARD MODULE</p><h1>{pageTitle(page)}</h1></div>
          </div>
          {page === "overview" && <OverviewPage snapshot={snapshot} runners={filteredRunners} />}
          {page === "runners" && <RunnersPage runners={filteredRunners} />}
          {page === "demographics" && <DemographicsPage runners={filteredRunners} />}
          {page === "location" && <LocationPage runners={filteredRunners} />}
          {page === "activity" && <ActivityPage runners={filteredRunners} />}
          {page === "quality" && <QualityPage snapshot={snapshot} status={status} />}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function pageTitle(page: PageId) {
  return {
    overview: "賽事概覽",
    runners: "跑者資料",
    demographics: "年齡與性別",
    location: "地點分布",
    activity: "時間與次數",
    quality: "資料品質",
  }[page];
}

function LandingHeader() {
  return (
    <header className="landing-header">
      <div className="brand">
        <span className="brand__mark"><i /><i /><i /><i /></span>
        <span><b>TATTA</b><small>INSIGHT_</small></span>
      </div>
      {/* <span className="internal-tag">● INTERNAL ONLY</span> */}
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div><strong>TATTA INSIGHT_</strong><span>內部跑者資料分析工具</span></div>
      <p>資料時間依瀏覽器所在地時區顯示 · 不保存個人層級歷史</p>
    </footer>
  );
}
